import { EditOutlined, InboxOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import {
	Button,
	Form,
	Input,
	Modal,
	Popconfirm,
	Segmented,
	Space,
	Table,
	Tag,
	Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { LeadFormAssignmentsDrawer } from "../components/LeadFormAssignmentsDrawer";
import { useArchiveLeadForm, useCreateLeadForm, useLeadFormsList } from "../hooks/useLeadForms";
import type { LeadForm } from "../types";

const { Title } = Typography;

interface CreateValues {
	name: string;
	description?: string;
}

export function LeadFormsPage() {
	const { t } = useTranslation("leadForms");
	const navigate = useNavigate();
	const [isActive, setIsActive] = useState(true);
	const [creating, setCreating] = useState(false);
	const [assigning, setAssigning] = useState<LeadForm | null>(null);
	const [form] = Form.useForm<CreateValues>();
	const list = useLeadFormsList(isActive);
	const create = useCreateLeadForm();
	const archive = useArchiveLeadForm();

	const handleCreate = (values: CreateValues) => {
		create.mutate(
			{ name: values.name, description: values.description ?? null },
			{
				onSuccess: (created) => {
					setCreating(false);
					form.resetFields();
					navigate({ to: "/lead-forms/$formId", params: { formId: created.id } });
				},
			}
		);
	};

	const columns: ColumnsType<LeadForm> = [
		{ title: t("list.columns.name"), dataIndex: "name", key: "name" },
		{
			title: t("list.columns.description"),
			dataIndex: "description",
			key: "description",
			render: (value: string | null) => value ?? <span className="text-gray-400">—</span>,
		},
		{
			title: t("list.columns.version"),
			dataIndex: "latestVersion",
			key: "latestVersion",
			// Versiyasiz forma normal holat: yaratilgan, lekin hali nashr
			// etilmagan (qoralama holati serverda yo'q — spec §7.2).
			render: (version: number | null) =>
				version === null ? (
					<Tag>{t("list.notPublished")}</Tag>
				) : (
					<Tag color="blue">{t("list.versionTag", { version })}</Tag>
				),
		},
		{
			title: t("list.columns.status"),
			dataIndex: "isActive",
			key: "isActive",
			render: (active: boolean) =>
				active ? (
					<Tag color="green">{t("list.active")}</Tag>
				) : (
					<Tag>{t("list.archivedStatus")}</Tag>
				),
		},
		{
			title: t("list.columns.actions"),
			key: "actions",
			align: "right",
			render: (_, row) => (
				<Space>
					<PermissionGate resource="lead-forms" action="read">
						<Button
							type="text"
							icon={<TeamOutlined />}
							title={t("list.assignmentsAction")}
							onClick={() => setAssigning(row)}
						/>
					</PermissionGate>
					<PermissionGate resource="lead-forms" action="read">
						<Button
							type="text"
							icon={<EditOutlined />}
							title={t("list.builderAction")}
							onClick={() => navigate({ to: "/lead-forms/$formId", params: { formId: row.id } })}
						/>
					</PermissionGate>
					{row.isActive && (
						<PermissionGate resource="lead-forms" action="update">
							<Popconfirm
								title={t("list.archiveConfirmTitle")}
								// Versiyalar O'CHIRILMAYDI — eski lidlar ularga
								// ishora qiladi (spec §7.2).
								description={t("list.archiveConfirmDescription")}
								okText={t("list.archiveConfirmOk")}
								cancelText={t("list.archiveConfirmCancel")}
								okButtonProps={{ danger: true }}
								onConfirm={() => archive.mutate(row.id)}
							>
								<Button
									type="text"
									danger
									icon={<InboxOutlined />}
									title={t("list.archiveAction")}
								/>
							</Popconfirm>
						</PermissionGate>
					)}
				</Space>
			),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Title level={3} className="!mb-0">
					{t("list.title")}
				</Title>
				<Space>
					<Segmented
						value={isActive ? "active" : "archived"}
						onChange={(value) => setIsActive(value === "active")}
						options={[
							{ value: "active", label: t("list.active") },
							{ value: "archived", label: t("list.archived") },
						]}
					/>
					<PermissionGate resource="lead-forms" action="create">
						<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
							{t("list.newForm")}
						</Button>
					</PermissionGate>
				</Space>
			</div>

			<Table<LeadForm>
				columns={columns}
				dataSource={list.data ?? []}
				rowKey="id"
				loading={list.isLoading}
				pagination={false}
				scroll={{ x: "max-content" }}
			/>

			<Modal
				open={creating}
				title={t("list.createModalTitle")}
				okText={t("list.createModalOk")}
				cancelText={t("list.createModalCancel")}
				confirmLoading={create.isPending}
				onCancel={() => setCreating(false)}
				onOk={() => form.submit()}
			>
				<Form form={form} layout="vertical" onFinish={handleCreate}>
					<Form.Item
						name="name"
						label={t("list.nameLabel")}
						rules={[{ required: true, message: t("list.nameRequired") }, { max: 200 }]}
					>
						<Input placeholder={t("list.namePlaceholder")} />
					</Form.Item>
					<Form.Item name="description" label={t("list.descriptionLabel")}>
						<Input.TextArea rows={3} maxLength={2000} />
					</Form.Item>
				</Form>
			</Modal>

			<LeadFormAssignmentsDrawer form={assigning} onClose={() => setAssigning(null)} />
		</div>
	);
}
